<template>
  <a-row :gutter="[16, 16]">
    <a-col span="24">
      <Filters
        ref="filtersComponent"
        :show-cycle-input="false"
        :show-duration-input="false"
        :show-steps-filter="true"
        :is-no-data-available="isNoDataAvailable"
        :is-analytics="true"
        @change="handleGetData"
      />
    </a-col>

    <a-col span="24">
      <a-divider orientation="left" class="mt-2">
        Station Analytics Dashboard
      </a-divider>

      <div class="d-flex">
        <span
          v-if="isDataNotFound && !isFetchingCycles"
          class="text-center mb-3"
          :class="{
            'w-50': showCompareAnalytics,
            'w-100': !showCompareAnalytics,
          }"
        >
          <a-typography :level="4"> No Data Available </a-typography>
        </span>
        <span
          v-if="
            isDataForComparisonNotFound && !isFetchingAnalyticsForComparison
          "
          class="text-center mb-3 w-50 ml-auto"
        >
          <a-typography :level="4"> No Data Available </a-typography>
        </span>
      </div>
    </a-col>

    <!-- Station Level Analytics Charts (4 charts in 2x2 grid) -->
    <a-col span="24">
      <StationLevelAnalytics
        :station-name="stationName"
        :analytics-data="stationLevelData"
        :loading="isLoadingStationAnalytics"
      />
    </a-col>

    <!-- Standard Deviation and Takt Related Chart side by side -->
    <a-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12">
      <StandardDeviationChart
        :std-deviation-data="stationLevelData"
        :loading="isLoadingStationAnalytics"
      />
    </a-col>

    <a-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12">
      <TaktRelatedChart :takt-data="taktData" :loading="isLoadingTaktData" />
    </a-col>

    <!-- Task Categorization Chart - Full Width -->
    <a-col span="24">
      <TaskCategorizationChart
        :categorization-data="categorizationData"
        :loading="isLoadingCategorizationData"
      />
    </a-col>
  </a-row>
</template>

<script>
import Filters from './Filters.vue';
import CardItem from 'src/components/shared/Components/CardItem.vue';
import ChartItem from '../Charts/ChartItem.vue';
import StationLevelAnalytics from './Charts/StationLevelAnalytics.vue';
import TaktRelatedChart from './Charts/TaktRelatedChart.vue';
import TaskCategorizationChart from './Charts/TaskCategorizationChart.vue';
import StandardDeviationChart from './Charts/StandardDeviationChart.vue';
import { deepClone } from 'src/utils/task';
import { cards, charts } from 'src/config/charts-config.js';
import { mapGetters, mapActions } from 'vuex';
import TelemetryService from 'src/services/telemetry';

export default {
  components: {
    Filters,
    CardItem,
    ChartItem,
    StationLevelAnalytics,
    TaktRelatedChart,
    TaskCategorizationChart,
    StandardDeviationChart,
  },
  inject: ['toast'],

  data() {
    return {
      chartList: deepClone(charts),
      cards: deepClone(cards),
      analytics_data: {},
      analyticsDataForComparison: {},
      isDataNotFound: false,
      isDataForComparisonNotFound: false,
      filterValues: null,
      filterValuesForComparison: null,
      // Station Analytics specific data
      stationName: 'Station 1',
      stationLevelData: [],
      taktData: [],
      categorizationData: [],
      isLoadingStationAnalytics: false,
      isLoadingTaktData: false,
      isLoadingCategorizationData: false,
    };
  },

  computed: {
    ...mapGetters([
      'organization',
      'devices',
      'isFetchingCycles',
      'showCompareAnalytics',
      'isFetchingAnalyticsForComparison',
      'taskProcesses',
      'deviceSerialNumToDisplayNameMap',
      'deviceDisplayNameToSerialNumMap',
    ]),

    charts() {
      const hide = [];
      return this.chartList.filter((chart) => !hide.includes(chart.title));
    },

    isNoDataAvailable() {
      return Object.keys(this.analytics_data).length === 0;
    },
  },

  methods: {
    ...mapActions([
      'setIsFetchingCycles',
      'setIsFetchingAnalyticsForComparison',
    ]),

    async handleGetData(filters) {
      if (filters.isComparing) {
        this.getAnalyticsForComparison(filters);
      } else {
        // Fetch Station Analytics specific data only
        await this.fetchStationAnalytics(filters);
      }
    },

    async fetchStationAnalytics(filters) {
      // Update station name from selected device
      if (filters.device) {
        this.stationName =
          this.deviceSerialNumToDisplayNameMap[filters.device] ||
          filters.device;
      }

      // Fetch all three chart data in parallel
      await Promise.all([
        this.fetchStationLevelData(filters),
        this.fetchTaktData(filters),
        this.fetchCategorizationData(filters),
      ]);
    },

    async fetchStationLevelData(filters) {
      this.isLoadingStationAnalytics = true;
      try {
        // Convert display name to serial number if needed
        const serialNumber =
          this.deviceDisplayNameToSerialNumMap[filters.device] ||
          filters.device;

        const params = {
          device_id: serialNumber,
          organization: filters.organization,
          datetime_start: filters.datetime_start,
          datetime_end: filters.datetime_end,
          cycle_type: filters.cycle_type || 'all_cycles',
          exclude_timeout_cycles: filters.exclude_timeout_cycles || false,
        };

        // Add task_ids if tasks are selected
        if (filters.tasks) {
          const taskIds = JSON.parse(filters.tasks);
          if (taskIds.length > 0) {
            params.task_ids = JSON.stringify(taskIds);
          }
        }

        const [error, data] = await TelemetryService.fetchStationAnalytics(
          params
        );

        if (error || !data) {
          this.toast.error('Failed to fetch station level analytics');
          this.stationLevelData = {};
          return;
        }

        // API returns: { labels, total_cycles, avg_cycle_time, total_work_time, avg_time_between_cycles }
        this.stationLevelData = data;
      } catch (error) {
        console.error('Error fetching station level data:', error);
        this.toast.error('Failed to fetch station level analytics');
        this.stationLevelData = {};
      } finally {
        this.isLoadingStationAnalytics = false;
      }
    },

    async fetchTaktData(filters) {
      this.isLoadingTaktData = true;
      try {
        // Convert display name to serial number if needed
        const serialNumber =
          this.deviceDisplayNameToSerialNumMap[filters.device] ||
          filters.device;

        const params = {
          device_id: serialNumber,
          organization: filters.organization,
          datetime_start: filters.datetime_start,
          datetime_end: filters.datetime_end,
          cycle_type: filters.cycle_type || 'all_cycles',
          exclude_timeout_cycles: filters.exclude_timeout_cycles || false,
        };

        // Add task_ids if tasks are selected
        if (filters.tasks) {
          const taskIds = JSON.parse(filters.tasks);
          if (taskIds.length > 0) {
            params.task_ids = JSON.stringify(taskIds);
          }
        }

        const [error, data] = await TelemetryService.fetchTaktTimeAnalytics(
          params
        );

        if (error || !data) {
          this.toast.error('Failed to fetch takt related data');
          this.taktData = {};
          return;
        }

        // API returns: { labels, avg_cycle_times, takt_times, percentage_diff }
        this.taktData = data;
      } catch (error) {
        console.error('Error fetching takt data:', error);
        this.toast.error('Failed to fetch takt related data');
        this.taktData = {};
      } finally {
        this.isLoadingTaktData = false;
      }
    },

    async fetchCategorizationData(filters) {
      this.isLoadingCategorizationData = true;
      try {
        // Convert display name to serial number if needed
        const serialNumber =
          this.deviceDisplayNameToSerialNumMap[filters.device] ||
          filters.device;

        const params = {
          device_id: serialNumber,
          organization: filters.organization,
          datetime_start: filters.datetime_start,
          datetime_end: filters.datetime_end,
          cycle_type: filters.cycle_type || 'all_cycles',
          exclude_timeout_cycles: filters.exclude_timeout_cycles || false,
        };

        const [error, data] = await TelemetryService.fetchTaskGroupingAnalytics(
          params
        );

        if (error || !data) {
          this.toast.error('Failed to fetch task categorization data');
          this.categorizationData = {};
          return;
        }

        // API returns nested structure: { Electric: { tasks: [...], total_cycles, ... }, ... }
        this.categorizationData = data;
      } catch (error) {
        console.error('Error fetching categorization data:', error);
        this.toast.error('Failed to fetch task categorization data');
        this.categorizationData = {};
      } finally {
        this.isLoadingCategorizationData = false;
      }
    },

    async getAnalytics(filters) {
      this.setIsFetchingCycles(true);
      const [error, data] = await TelemetryService.fetchCyclesDetailsV2(
        false,
        filters
      );

      this.filterValues = filters;
      this.setIsFetchingCycles(false);

      if ((data?.body && data?.body == 'No result found') || error) {
        this.clearCharts();
        this.clearCards();
        this.analytics_data = {};
        this.isDataNotFound = true;
        return;
      }

      this.isDataNotFound = false;
      this.analytics_data = data;
      this.updateCards();
      this.updateCharts();
    },

    async getAnalyticsForComparison(filters) {
      this.setIsFetchingAnalyticsForComparison(true);
      const [error, data] = await TelemetryService.fetchCyclesDetailsV2(
        false,
        filters
      );

      this.filterValuesForComparison = filters;

      if ((data?.body && data?.body == 'No result found') || error) {
        this.clearCards(true);
        this.clearCharts(false);
        this.analyticsDataForComparison = {};
        this.isDataForComparisonNotFound = true;
        this.setIsFetchingAnalyticsForComparison(false);
        return;
      }

      this.isDataForComparisonNotFound = false;
      this.analyticsDataForComparison = data;
      this.updateCards(true);
      this.updateCharts(true);
      this.setIsFetchingAnalyticsForComparison(false);
    },

    updateCards(isComparing = false) {
      // Implement card updates based on analytics data
      console.log('Updating cards for Station Analytics', isComparing);
    },

    updateCharts(isComparing = false) {
      // Implement chart updates based on analytics data
      console.log('Updating charts for Station Analytics', isComparing);
    },

    clearCards(isComparing = false) {
      // Clear card data
      console.log('Clearing cards', isComparing);
    },

    clearCharts() {
      // Clear chart data
      const chartList = [...this.chartList];
      for (let chart in chartList) {
        chartList[chart].data = '';
        chartList[chart].labels = '';
        chartList[chart].stepsAvgTime = '';
        chartList[chart].stepsStdDev = '';
      }
      this.chartList = chartList;
    },
  },
};
</script>
